import type { Type } from '@angular/core'
import type * as ec from '@attain-apps/fe/web/main/lib/entity-collection'

import type * as tableModels from '../models'

/**
 * Collection-wide rules for adapting entities and attributes into a table.
 *
 * This normalized plugin configuration is shared by every table view that
 * renders the collection. Presentation behavior belongs to {@link View} so
 * one collection can support multiple table instances without coupling its
 * row/cell mapping to a particular view.
 */
export type Config<TTypeParams extends ec.models.TypeParams.Constraint> = Omit<
  Config.Options<TTypeParams>,
  'columnConfigs' | 'rowConfig'
> &
  Required<Pick<Config.Options<TTypeParams>, 'columnConfigs' | 'rowConfig'>>

export namespace Config {
  /**
   * Applies defaults required by collection-wide row and column mapping.
   *
   * Per-table presentation is intentionally excluded: it is supplied through
   * {@link Input} and may differ for each component rendering this collection.
   */
  export function from<TTypeParams extends ec.models.TypeParams.Constraint>({
    cellConfig,
    columnConfigs = {},
    rowConfig = () => ({}),
  }: Options<TTypeParams> = {}): Config<TTypeParams> {
    return {
      cellConfig,
      columnConfigs,
      rowConfig,
    }
  }

  /** Collection-wide options accepted by the entity-collection table factory. */
  export interface Options<
    TTypeParams extends ec.models.TypeParams.Constraint,
  > {
    readonly cellConfig?: Cell.Factory.Optional<TTypeParams>
    readonly columnConfigs?: Partial<Record<string, Column<TTypeParams>>>
    readonly rowConfig?: Row.Factory<TTypeParams>
  }

  /** Presentation behavior for one table instance backed by a collection. */
  export interface View<TTypeParams extends ec.models.TypeParams.Constraint> {
    readonly container?: tableModels.Layout.Container
    readonly creation?: Creation<TTypeParams>
    readonly pagination?: boolean
    readonly search?: boolean | NonNullable<tableModels.Search['options']>
    readonly selection?: boolean
    /** Selection lookup used by table rows and collection-backed tools. */
    readonly selectionTarget?: ec.models.Target
    readonly showColumnSettings?: boolean
    readonly supportedPageSizes?: readonly number[]
    readonly title?: string
    readonly toolbarTools?: readonly tableModels.Tool[]
    readonly wrap?: boolean
  }
  export namespace View {
    /** Legacy page-size choices used when an enabled view has no override. */
    export const DEFAULT_SUPPORTED_PAGE_SIZES: readonly number[] = [
      20, 50, 100, 200,
    ]
  }

  /**
   * Entity-collection variant of the canonical table input.
   *
   * Collection mapping and individual presentation options are deliberately
   * separate so one collection can back multiple table views.
   */
  export interface Input<
    TTypeParams extends
      ec.models.TypeParams.Constraint = ec.models.TypeParams.Constraint,
  > {
    readonly collection: ec.Collection<TTypeParams>
    readonly view?: View<TTypeParams>
  }
  export namespace Input {
    /**
     * Entity-collection input accepted by the generic table component.
     *
     * `Collection<T>` is invariant, so TypeScript cannot express one union
     * member that accepts every concrete collection type. The table erases the
     * collection parameter only at this Angular input boundary; each consumer
     * still declares its own `Input<TTypeParams>` configuration.
     */
    export type ForTable = Input<any>
  }

  export type Creation<TTypeParams extends ec.models.TypeParams.Constraint> =
    | boolean
    | ((create: (input: TTypeParams['createEntity']) => void) => void)

  /** Configuration produced for one entity row. */
  export interface Row {
    readonly css?: tableModels.Styles
    readonly iconsConfigs?: readonly Row.IconConfig[]
    readonly onClick?: () => void
  }
  export namespace Row {
    export interface Context<
      TTypeParams extends ec.models.TypeParams.Constraint,
    > {
      readonly entity: TTypeParams['entity']
      readonly rowIdx: number
    }

    export type Factory<TTypeParams extends ec.models.TypeParams.Constraint> = (
      context: Context<TTypeParams>,
    ) => Row

    /** Legacy-shaped row icon mapped to a canonical tool descriptor. */
    export interface IconConfig {
      readonly class: string
      readonly disabled?: boolean
      readonly loading?: boolean
      readonly onClick: () => void
      readonly title?: string
    }
  }

  /** Configuration produced for one entity attribute cell. */
  export interface Cell {
    readonly component?: Type<unknown>
    /** Optional inline styles applied to the cell renderer. */
    readonly css?: tableModels.Styles
    readonly inputs?: tableModels.Renderer.Component['inputs']
    readonly onClick?: () => void
    readonly outputs?: tableModels.Renderer.Component['outputs']
  }
  export namespace Cell {
    export interface Context<
      TTypeParams extends ec.models.TypeParams.Constraint,
    > extends Row.Context<TTypeParams> {
      readonly attribute: ec.models.Attribute<TTypeParams>
      readonly value: unknown
    }

    export type Factory<TTypeParams extends ec.models.TypeParams.Constraint> = (
      context: Context<TTypeParams>,
    ) => Cell

    export namespace Factory {
      export type Optional<
        TTypeParams extends ec.models.TypeParams.Constraint,
      > = (context: Context<TTypeParams>) => Cell | undefined | void
    }
  }

  /** Attribute-specific overrides applied to one canonical column. */
  export interface Column<TTypeParams extends ec.models.TypeParams.Constraint>
    extends Pick<
      tableModels.Column<TTypeParams['entity']>,
      'align' | 'resizable' | 'width'
    > {
    readonly cellConfig?: Cell.Factory<TTypeParams>
    readonly isSortable?: boolean
  }
}
